import React from 'react';

import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface BreadcrumbNode {
  label: string;
  href: string;
  onClick?: () => void;
}

/** Props for an optional SPA link component (e.g. react-router `Link`). */
export type BreadcrumbLinkComponentProps = {
  to: string;
  className?: string;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLElement>;
};

interface DynamicBreadcrumbProps {
  path: BreadcrumbNode[];
  maxChars?: number;
  /** When set, uses this instead of `<a href>` for client-side routing. */
  LinkComponent?: React.ComponentType<BreadcrumbLinkComponentProps>;
  lastItemAsLink?: boolean;
}

export function AutoBreadcrumb({ path, maxChars = 20, LinkComponent, lastItemAsLink = true }: DynamicBreadcrumbProps) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {path.map((node, index) => {
          const isLast = index === path.length - 1;
          const isLengthy = node.label.length > maxChars;

          return (
            <React.Fragment key={`${index}-${node.href}`}>
              <BreadcrumbItem>
                {isLast ? (
                  lastItemAsLink ? (
                    LinkComponent ? (
                      <BreadcrumbLink asChild>
                        <LinkComponent to={node.href} {...(node.onClick && { onClick: node.onClick })}>
                          {node.label}
                        </LinkComponent>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbLink href={node.href} {...(node.onClick && { onClick: node.onClick })}>
                        {node.label}
                      </BreadcrumbLink>
                    )
                  ) : (
                    <BreadcrumbPage className="max-w-[200px] sm:max-w-[400px] truncate" title={node.label}>
                      {node.label}
                    </BreadcrumbPage>
                  )
                ) : isLengthy ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex items-center gap-1">
                      <BreadcrumbEllipsis className="h-4 w-4" />
                      <span className="sr-only">Toggle {node.label}</span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {LinkComponent ? (
                        <DropdownMenuItem asChild>
                          <LinkComponent to={node.href} {...(node.onClick && { onClick: node.onClick })}>
                            {node.label}
                          </LinkComponent>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem>
                          <a href={node.href} {...(node.onClick && { onClick: node.onClick })}>
                            {node.label}
                          </a>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : LinkComponent ? (
                  <BreadcrumbLink asChild>
                    <LinkComponent to={node.href} {...(node.onClick && { onClick: node.onClick })}>
                      {node.label}
                    </LinkComponent>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbLink href={node.href} {...(node.onClick && { onClick: node.onClick })}>
                    {node.label}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
